import * as React from "react"
import {
  Alert,
  AlertTitle,
  Button,
  Collapse,
  IconButton,
  Stack,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import { useTranslation } from "react-i18next"

type Variant = "info" | "warning" | "success"

export type TicketFallbackBannerProps = {
  /** Mostra/oculta o banner */
  open: boolean

  /** Estilo do banner (mapeia para severities do MUI) */
  variant?: Variant

  /** Título (se não vier, usa um padrão por variante) */
  title?: string

  /** Mensagem principal */
  message: string

  /** Texto do botão de ação (ex.: "Escolher outra opção") */
  actionLabel?: string

  /** Callback do botão de ação */
  onAction?: () => void

  /** Callback ao fechar (ícone X) */
  onClose?: () => void

  /** Layout mais compacto */
  dense?: boolean
}

const defaultTitleKey = {
  info: "ticket.bannerInfo",
  warning: "ticket.bannerWarning",
  success: "ticket.bannerSuccess",
} as const

const iconByVariant: Record<Variant, React.ReactNode> = {
  info: <InfoOutlinedIcon fontSize="inherit" />,
  warning: <WarningAmberOutlinedIcon fontSize="inherit" />,
  success: <CheckCircleOutlineIcon fontSize="inherit" />,
}

const severityByVariant: Record<Variant, "info" | "warning" | "success"> = {
  info: "info",
  warning: "warning",
  success: "success",
}

export function TicketFallbackBanner({
  open,
  variant = "info",
  title,
  message,
  actionLabel,
  onAction,
  onClose,
  dense = false,
}: TicketFallbackBannerProps) {
  const { t } = useTranslation()

  return (
    <Collapse in={open} unmountOnExit>
      <Alert
        severity={severityByVariant[variant]}
        icon={iconByVariant[variant]}
        sx={{
          borderRadius: 2,
          alignItems: "flex-start",
          py: dense ? 0.75 : 1,
          px: dense ? 1.25 : 1.5,
        }}
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            {actionLabel && onAction ? (
              <Button
                size={dense ? "small" : "medium"}
                onClick={onAction}
                variant="text"
                sx={{ whiteSpace: "nowrap" }}
              >
                {actionLabel}
              </Button>
            ) : null}

            {onClose ? (
              <IconButton
                size="small"
                onClick={onClose}
                aria-label={t("ticket.bannerCloseAria")}
                sx={{ mt: 0.25 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            ) : null}
          </Stack>
        }
      >
        <AlertTitle sx={{ mb: 0.5, fontWeight: 700 }}>
          {title ?? t(defaultTitleKey[variant])}
        </AlertTitle>
        {message}
      </Alert>
    </Collapse>
  )
}
